import { UserContext } from '@components/contexts/UserProvider';
import { useHasPermission } from '@lib/hooks/useHasPermission';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, ButtonToolbar, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import ExecutionSummaryCard from './ExecutionSummaryCard';
import { formatToDecimals } from '@lib/helpers/formatting';
import { IcoArrowClockwise, IcoDownloadCloudArrowSmall, IcoReported, IcoReportedPartially } from '@icons';
import ModalDownloadReportTimesheets from './Sampling/ModalDownloadReportTimesheets';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import { useParams } from 'react-router-dom';
import { getProjectResumeTimesheets, getProjectResumeTotals } from '@lib/api';
import { AgGridReact } from 'ag-grid-react';
import CustomPaginationMui from '@components/materialComponents/CustomPaginationMui';
import Loading from '@components/atomic/atoms/Loading';
import { columnCentered, columnToRight, customCellStyle } from '@lib/helpers/agGridHelpers';
import { months } from '@lib/helpers/calendar';
// import OverlayTriggerStopLight from './OverlayTriggerStopLight';
import ResumeChecklistCellRenderer from './ResumeChecklistCellRenderer';
import SummaryRecordModal from './SummaryRecordModal';
import CustomSearch from './Sampling/CustomFilter/CustomSearch';
import FiltersToDisplay from './Sampling/CustomFilter/FiltersToDisplay';
import CustomFilter from './Sampling/CustomFilter/CustomFilter';
import TotalResumeChecklistCellRenderer from './TotalResumeChecklistCellRenderer';
// import SummaryInternalSuppliesFilter from './SummaryInternalSuppliesFilter';

type MetaType = {
	meta: {
		total: number;
		per_page: number;
		current_page: number;
		last_page: number;
	};
};

type TotalsType = {
	total: number;
	totalRend: number;
	totalFt: number;
	totalRendFt: number;
	totalTs: number;
	totalRendTs: number;
	totalTsIndirect: number;
};

type FilteredTotalType = {
	all: number;
	rend: number;
	totalFtIndirect: number;
	totalTsIndirect: number;
};

interface SummaryInternalSuppliesProps {
	project: {
		name: string;
		sco: boolean;
	};
}

const SummaryInternalSupplies: React.FC<SummaryInternalSuppliesProps> = ({ project }) => {
	const { userAccess } = useContext(UserContext);
	const canViewMacroInfo = useHasPermission(['view-control-macro-information']);
	const [filteredTotal, setFilteredTotal] = useState<FilteredTotalType | null>(null);
	const [queryParams, setQueryParams] = useState<string | undefined | null>();
	const [activeFilters, setActiveFilters] = useState({});

	const internalSuppliesRef = useRef<AgGridReact>(null);

	const localeText = AG_GRID_LOCALE_IT;
	const defaultColDef = {
		resizable: true,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
		},
		sortable: true,
		filterParams: {
			suppressAndOrCondition: true, //for disable AND/OR login in filter
			filterOptions: ['contains'],
		},
		suppressCellFocus: false,
		suppressHeaderFocus: false,
		// flex: 1,
		// minWidth: 250,
	};
	const { projectId } = useParams<{ projectId: string }>();

	const { data: totals, isPending: isPendingTotals } = useQuery<{ data: TotalsType }, Error, TotalsType>({
		queryKey: ['totals', projectId],
		queryFn: () => getProjectResumeTotals(projectId),
		select: (resp) => resp.data,
	});

	const [internalSupplies, setInternalSupplies] = useState([]);
	const [internalSuppliesMeta, setInternalSuppliesMeta] = useState<MetaType>({} as MetaType);

	const [showModalDownloadReportTimesheets, setShowModalDownloadReportTimesheets] = useState(false);

	const queryClient = useQueryClient();

	const {
		data: staff,
		isPending: isPendingStaff,
		isRefetching: isRefetchingStaff,
	} = useQuery({
		queryKey: ['staff', projectId, queryParams],
		queryFn: () => getProjectResumeTimesheets(projectId, queryParams, 'staff'),
	});

	useEffect(() => {
		if (staff && staff.success) {
			setInternalSupplies(
				staff.data.sort((a, b) => {
					return a.id - b.id;
				}),
			);
			const { data, success, totals, ...restResp } = staff;
			setInternalSuppliesMeta(restResp);
			!!queryParams ? setFilteredTotal(totals) : setFilteredTotal(null);
		}
	}, [staff, queryParams]);

	useEffect(() => {
		let timer = null;
		if (isPendingStaff || isRefetchingStaff) {
			timer = setTimeout(() => {
				internalSuppliesRef.current?.api?.showLoadingOverlay();
			}, 100);
		} else {
			internalSuppliesRef.current?.api?.hideOverlay();
		}
		return () => clearTimeout(timer);
	}, [isPendingStaff, isRefetchingStaff]);

	const ReportedCheckRenderer = (params) => {
		const isReporting = params.data.reporting;
		const isReported = params.value;
		return (
			<div
				style={{
					display: 'grid',
					placeContent: 'center',
					height: '100%',
				}}
			>
				{isReporting ? <IcoReportedPartially /> : <></>}
				{isReported ? <IcoReported /> : <></>}
			</div>
		);
	};

	const originalWidths = useRef<Record<string, number>>({});

	const saveOriginalWidths = useCallback(() => {
		const columnApi = internalSuppliesRef.current?.columnApi;
		if (!columnApi) return;

		columnApi.getAllColumns()?.forEach((col) => {
			originalWidths.current[col.getColId()] = col.getActualWidth();
		});
	}, []);
	const restoreWidths = useCallback(() => {
		const columnApi = internalSuppliesRef.current?.columnApi;
		if (!columnApi) return;

		Object.entries(originalWidths.current).forEach(([colId, width]) => {
			columnApi.setColumnWidth(colId, width, false);
		});
	}, []);
	const updateGridLayout = useCallback(() => {
		const api = internalSuppliesRef.current?.api;
		if (!api) return;
		const FIT_THRESHOLD = 1920;

		if (window.innerWidth > FIT_THRESHOLD) {
			saveOriginalWidths();
			api.sizeColumnsToFit();
		} else {
			restoreWidths();
		}
	}, [restoreWidths, saveOriginalWidths]);

	useEffect(() => {
		window.addEventListener('resize', updateGridLayout);
		return () => window.removeEventListener('resize', updateGridLayout);
	}, [updateGridLayout]);

	const columnDefsInternalSupplies = [
		...(userAccess.isAboveAuditor
			? [
					{
						headerName: '',
						field: 'reported',
						minWidth: 56,
						width: 56,
						maxWidth: 56,
						cellRenderer: ReportedCheckRenderer,
						...columnCentered(),
					},
				]
			: []),
		// ...(userAccess.isAboveAuditor
		// 	? [
		// 			{
		// 				headerName: '',
		// 				field: 'reportable',
		// 				minWidth: 56,
		// 				width: 56,
		// 				maxWidth: 56,
		// 				cellRenderer: (params: {
		// 					data: {
		// 						timesheet: {
		// 							checkliststatus: {
		// 								color: string;
		// 							};
		// 						};
		// 					};
		// 				}) => {
		// 					return <OverlayTriggerStopLight checklistColor={params.data.timesheet.checkliststatus?.color} />;
		// 				},
		// 				// cellRenderer: TotalResumeChecklistCellRenderer,
		// 				cellStyle: { ...customCellStyle },
		// 			},
		// 		]
		// 	: []),
		{
			headerName: 'Dipendente',
			field: 'employee.keyValue',
			filter: 'agTextColumnFilter',
			cellStyle: { ...customCellStyle },
		},
		{
			headerName: 'Anno',
			field: 'year',
			headerClass: 'text-center',
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
			maxWidth: 100,
		},
		{
			headerName: 'Mese',
			field: 'month',
			headerClass: 'text-center',
			cellStyle: { ...customCellStyle, justifyContent: 'center' },
			valueGetter: (params) => {
				return months()[params.data.month - 1];
			},
		},
		{
			headerName: 'Categoria',
			field: 'category',
			filter: 'agTextColumnFilter',
			cellStyle: {
				...customCellStyle,
			},
		},
		{
			headerName: 'Record',
			field: 'timesheet',
			cellRenderer: ResumeChecklistCellRenderer,
			cellRendererParams: {
				project,
				modalComponent: SummaryRecordModal,
			},
			// cellRenderer: ResumeChecklistCellRenderer,
			// cellRendererParams: {
			// 	link: true,
			// 	setShow: setShow,
			// 	setApiToCall: setApiToCall,
			// 	queryParam: 'number',
			// },
			minWidth: 300,
			filter: 'agTextColumnFilter',
			filterValueGetter: (params) => {
				return params.data.timesheet.keyValue;
			},
			comparator: (valueA, valueB) => {
				if (valueA.keyValue === valueB.keyValue) return 0;
				return valueA.keyValue > valueB.keyValue ? 1 : -1;
			},
		},
		{
			headerName: 'Beneficiario',
			field: 'beneficiary.name',
			minWidth: 170,
			filter: 'agTextColumnFilter',
			filterValueGetter: (params) => {
				return params.data.beneficiary.name;
			},
			cellStyle: {
				...customCellStyle,
			},
		},
		{
			headerName: 'Costo totale €',
			field: 'totalCost',
			valueFormatter: (params) => {
				return formatToDecimals(params.data.totalCost);
			},
			// cellRendererParams: {
			// 	queryParam: 'total_cost',
			// 	typeOf: 'number',
			// },
			filter: 'agTextColumnFilter',
			filterParams: {
				filterOptions: ['equals'],
				textMatcher: ({ filterOption, value, filterText }) => {
					if (filterText == null) return false;

					switch (filterOption) {
						case 'equals':
							return value === filterText || formatToDecimals(Number(value)) === filterText;
						default:
							return false;
					}
				},
			},
			comparator: (_valueA, _valueB, nodeA, nodeB) => {
				if (nodeA.data.totalCost === nodeB.data.totalCost) return 0;
				return nodeA.data.totalCost > nodeB.data.totalCost ? 1 : -1;
			},
			cellStyle: {
				...customCellStyle,
				justifyContent: 'right',
			},
			...columnToRight(),
		},
		...(userAccess.isAboveAuditor
			? [
					{
						headerName: 'Stato del controllo',
						field: 'reportable',
						cellRenderer: (params) => {
							return <TotalResumeChecklistCellRenderer {...params} />;
						},
						// cellRendererParams: {
						// 	queryParam: 'reportable',
						// },
						minWidth: 301,
						...columnCentered(),
						cellStyle: {
							whiteSpace: 'initial',
							justifyContent: 'center',
						},
						pinned: 'right',
						suppressSizeToFit: true,
					},
				]
			: []),
	];

	const options = [
		{
			value: 'white',
			label: 'Da lavorare',
		},
		{
			value: 'gray',
			label: 'In lavorazione',
		},
		{
			value: 'red',
			label: 'Non conforme',
		},
		{
			value: 'yellow',
			label: 'Incompleta',
		},
		{
			value: 'green',
			label: 'Conforme',
		},
		{
			value: 'green_white',
			label: 'Conforme - da validare',
		},
		{
			value: 'green_white_settled',
			label: 'Conforme - validato',
		},
	];
	const reportedOptions = [
		{
			value: '1',
			label: 'Rendicontati',
		},
		{
			value: '0',
			label: 'Non rendicontati',
		},
		{
			value: 'none',
			label: 'Nessun filtro',
		},
	];
	const reportableOptions = [
		{
			value: '1',
			label: 'Eleggibili',
		},
		{
			value: '0',
			label: 'Non eleggibili',
		},
		{
			value: 'none',
			label: 'Nessun filtro',
		},
	];
	const documentOptions = [
		{
			value: '1',
			label: 'Presente',
		},
		{
			value: '0',
			label: 'Assente',
		},
		{
			value: 'none',
			label: 'Nessun filtro',
		},
	];

	const reportedOption = {
		name: 'reported',
		label: 'Rendicontazione',
		type: 'boolean' as const,
		options: reportedOptions,
	};
	const reportableOption = {
		name: 'reportable',
		label: 'Eleggibilità',
		type: 'boolean' as const,
		options: reportableOptions,
	};

	const statusOption = {
		name: 'status',
		label: 'Stato',
		options,
		type: 'static' as const,
		needsEntityType: true,
	};
	const controlOption = {
		name: 'control',
		label: 'Domanda contenuta',
		type: 'control' as const,
		needsEntityType: true,
	};

	const hasCedolinoOption = {
		name: 'has_document',
		extraQueryParams: [
			{
				name: 'document_codes',
				value: 'TS_CEDOLINO',
			},
		],
		label: 'Cedolino paga',
		switchLabelPrefix: 'Filtra per',
		switchLabelSuffix: 'con Cedolino',
		type: 'boolean-extra' as const,
		options: documentOptions,
	};
	const hasTimesheetOption = {
		name: 'has_document',
		extraQueryParams: [
			{
				name: 'document_codes',
				value: 'TS_TIMESHEET',
			},
		],
		label: 'Timesheet',
		switchLabelPrefix: 'Filtra per',
		switchLabelSuffix: 'con Timesheet',
		type: 'boolean-extra' as const,
		options: documentOptions,
	};
	const hasCostDetailsOption = {
		name: 'has_document',
		extraQueryParams: [
			{
				name: 'document_codes',
				value: 'TS_COSTDETAILS',
			},
		],
		label: 'Dettaglio costi del personale',
		switchLabelPrefix: 'Filtra per',
		switchLabelSuffix: 'con Dettaglio costi del personale',
		type: 'boolean-extra' as const,
		options: documentOptions,
	};

	const filterFields = [
		{
			name: 'timesheet',
			label: 'Records',
			options,
			queryParamName: 'timesheets_color',
			entityType: 'timesheets',
			needsEntityType: true,
			pluralLabel: 'record',
			optionsType: 'static' as const,
			advancedOptions: [statusOption, controlOption, hasCedolinoOption, hasTimesheetOption, hasCostDetailsOption],
		},
		{
			name: 'reported',
			label: 'Rendicontazione',
			queryParamName: 'reported',
			type: 'boolean' as const,
			options: reportedOptions,
			booleanLabel: 'Elementi rendicontati',
			advancedOptions: [reportedOption],
		},
		{
			name: 'reportable',
			label: 'Eleggibilità',
			queryParamName: 'reportable',
			type: 'boolean' as const,
			options: reportableOptions,
			booleanLabel: 'Elementi eleggibili',
			advancedOptions: [reportableOption],
		},
	];

	// const RefreshButton = () => {
	// 	return (
	// 		<OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
	// 			<Button
	// 				onClick={() => {
	// 					queryClient.invalidateQueries({ queryKey: ['staff', projectId] });
	// 				}}
	// 				className="btn custom-ghost-button"
	// 				style={{
	// 					fontWeight: 500,
	// 					fontSize: '18px',
	// 					display: 'flex',
	// 					alignItems: 'flex-start',
	// 					justifyContent: 'center',
	// 					gap: '0.5rem',
	// 					textTransform: 'none',
	// 					padding: 0,
	// 				}}
	// 			>
	// 				<IcoArrowClockwise />
	// 				ricarica dati
	// 			</Button>
	// 		</OverlayTrigger>
	// 	);
	// };

	return (
		<Fragment>
			{userAccess.isAboveAuditor ? (
				<Row
					className="align-items-center mb-3"
					style={{
						marginTop: '13px',
					}}
				>
					{canViewMacroInfo ? (
						isPendingTotals ? (
							<Loading fixedHeight="180px" />
						) : (
							<Row>
								<Col>
									<ExecutionSummaryCard
										title="Avanzamento"
										items={[
											{
												key: 'total',
												label: 'Spesa effettuata:',
												value: formatToDecimals(totals.total) + ' €',
											},
											{
												key: 'eligible',
												label: 'Di cui eleggibile:',
												value: formatToDecimals(totals.totalRend) + ' €',
											},
										]}
									/>
								</Col>
								<Col>
									<ExecutionSummaryCard
										title="Costi Interni"
										items={[
											{
												key: 'intern-workers-expenses',
												label: 'Spesa in costi interni:',
												value: formatToDecimals(totals.totalTs) + ' €',
											},
											{
												key: 'intern-workers-eligible',
												label: 'Di cui eleggibile:',
												value: formatToDecimals(totals.totalRendTs),
											},

											...(!!project.sco
												? [
														{
															key: 'intern-workers-indirect-costs',
															label: 'Costi indiretti',
															value: formatToDecimals(totals.totalTsIndirect) + ' €',
														},
													]
												: []),
										]}
									/>
								</Col>
								<Col>
									<ExecutionSummaryCard
										title="Filtro"
										items={[
											{
												key: 'intern-workers-filtered',
												label: 'Spesa filtrata:',
												value: `${!!filteredTotal ? formatToDecimals(filteredTotal.all) : formatToDecimals(totals.totalTs)} €`,
											},
											{
												key: 'intern-workers-filtered-eligible',
												label: 'Di cui eleggibile:',
												value: `${!!filteredTotal ? formatToDecimals(filteredTotal.rend) : formatToDecimals(totals.totalRendTs)} €`,
											},
											...(!!project.sco
												? [
														{
															key: 'intern-workers-indirect-costs',
															label: 'Costi indiretti',
															value: `${!!filteredTotal ? formatToDecimals(filteredTotal.rend) : formatToDecimals(totals.totalTsIndirect)} €`,
														},
													]
												: []),
										]}
									/>
								</Col>
							</Row>
						)
					) : null}
				</Row>
			) : null}

			<Row
				style={{
					marginBlock: '40px',
				}}
			>
				<Col>
					<span style={{ fontWeight: 700 }}>Fai click sugli elementi nelle celle per eseguire le azioni di controllo.</span>
					<br />
					Fai click sugli elementi nelle celle per eseguire le azioni di controllo.
				</Col>
				<Col
					style={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<div
						style={{
							marginLeft: 'auto',
						}}
					>
						<OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
							<Button
								onClick={() => {
									queryClient.invalidateQueries({ queryKey: ['staff', projectId] });
								}}
								className="btn custom-ghost-button"
								style={{
									fontWeight: 500,
									fontSize: '18px',
									display: 'flex',
									alignItems: 'flex-start',
									justifyContent: 'center',
									gap: '0.5rem',
									textTransform: 'none',
									padding: 0,
								}}
							>
								<IcoArrowClockwise />
								ricarica dati
							</Button>
						</OverlayTrigger>
					</div>
				</Col>
			</Row>

			<Row
				style={{
					marginTop: '3rem',
					marginBottom: '2rem',
				}}
			>
				<Col className="col-4">
					{/* <CustomQuickFilterBox
								tableRef={staffRef}
								dataMeta={staffMeta}
								setData={setStaff}
								dataLength={staff?.length}
								isFirstResearch={isFirstResearch2}
								setIsFirstResearch={setIsFirstResearch2}
								refTable="staff"
								isRefreshing={isLoading}
								setIsRefreshing={setIsLoading}
							/> */}
					{/* <CustomSearch
						activeFilters={activeFilters}
						setActiveFilters={setActiveFilters}
						setQueryParams={setQueryParams}
						isLoading={isPendingStaff || isRefetchingStaff || isPendingTotals}
					/> */}
				</Col>
				<Col className="col-8 text-right py-1">
					<ButtonToolbar className="justify-content-end align-items-center" style={{ height: '38px', gap: '1.5rem' }}>
						{/* <RefreshButton /> */}
						<CustomSearch
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							setQueryParams={setQueryParams}
							isLoading={isPendingStaff || isRefetchingStaff || isPendingTotals}
						/>
						<CustomFilter
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							filterFields={filterFields}
							pageName="Controllo"
							projectName={project.name}
							queryParams={queryParams}
							setQueryParams={setQueryParams}
							hasNewButton
						/>
						{/* <OEFilterStaff
									columnDefs={columnDefsStaff}
									projectId={project.id}
									setTable={setStaff}
									setTableMeta={setStaffMeta}
									isFiltered={isFiltered}
									setIsFiltered={setIsFiltered}
									setFilteredTotal={setFilteredTotal}
									handleClick={() => handleClick(filter2Ref)}
									setFilterQueryParamsString={setFilterQueryParamsString}
									tableRef={staffRef}
									ref={filter2Ref}
								/> */}
						<Button
							onClick={() => {
								setShowModalDownloadReportTimesheets(true);
							}}
							className="btn custom-ghost-button"
							style={{
								fontWeight: 500,
								fontSize: '18px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '0.5rem',
								textTransform: 'none',
								padding: 0,
							}}
						>
							<IcoDownloadCloudArrowSmall />

							<span style={{ translate: '0 1px' }}>scarica</span>
						</Button>

						<ModalDownloadReportTimesheets
							filterQueryParamsString={queryParams}
							projectId={projectId}
							setShowModalDownloadReportTimesheets={setShowModalDownloadReportTimesheets}
							showModalDownloadReportTimesheets={showModalDownloadReportTimesheets}
						/>
					</ButtonToolbar>
				</Col>
			</Row>
			<FiltersToDisplay
				activeFilters={activeFilters}
				setActiveFilters={setActiveFilters}
				queryParams={queryParams}
				setQueryParams={setQueryParams}
				filterFields={filterFields}
			/>

			{Array.isArray(internalSupplies) ? (
				<Row className="mb-5">
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						{/* {Object.keys(internalSuppliesMeta).length > 0 ? ( */}
						<div className="custom-pinned_right-table ag-theme-alpine list-table" style={{ height: '800px', position: 'relative' }}>
							{/* N.D.R.: hack for header + cell box-shadow */}
							<div
								style={{
									position: 'absolute',
									top: 0,
									bottom: 0,
									right: 0,
									zIndex: 999,
									boxShadow: '-10px 0px 10px -5px #0000004d',
									width: internalSupplies.length > 6 ? '315px' : '300px',
									pointerEvents: 'none',
									transition: 'width 0.3s ease-in-out',
								}}
							/>
							<AgGridReact
								ref={internalSuppliesRef}
								columnDefs={columnDefsInternalSupplies}
								rowData={internalSupplies}
								defaultColDef={defaultColDef}
								tooltipShowDelay={0}
								tooltipHideDelay={2000}
								tooltipMouseTrack={true}
								// onFilterChanged={onFilterChanged}
								loadingOverlayComponent={() => <Loading />}
								headerHeight={60}
								rowHeight={104}
								suppressCellFocus={true}
								rowSelection="single"
								localeText={localeText}
								onFirstDataRendered={updateGridLayout}
							/>
							{Object.keys(internalSuppliesMeta).length > 0 && (
								<CustomPaginationMui
									dataMeta={internalSuppliesMeta}
									setData={setInternalSupplies}
									setDataMeta={setInternalSuppliesMeta}
									refTable="staff"
									setIsFirstResearch={() => {}}
									setIsChangedPagination={() => {}}
								/>
							)}
						</div>
						{/* ) : (
							<Row className="text-center my-5">
								<span>Non sono presenti elementi caricati</span>
							</Row>
						)} */}
					</Col>
				</Row>
			) : (
				<Loading fixedHeight="50vh" />
			)}
		</Fragment>
	);
};

export default SummaryInternalSupplies;
